import { TransitionDto, TransitionKind } from "@repo/api/transition";
import { useCallback, useState } from "react";
import TransitionTriggerButton from "./TransitionTriggerButton";
import TransitionDialog from "./TransitionDialog";
import { TransitionFormConfig } from "@repo/video-engine-i2v/components/ImageTransitionBuilder/ImageTransitionBuilder";
import { UseTransitionPreview } from "./TransitionPreview";
import {
  CompositionTransitionType,
  getCompositionTransitionBuilder,
} from "@repo/video-engine-i2v/components/CompositionTransitionBuilder/CompositionTransitionBuilder";
import CompositionTransitionComposition, {
  CompositionTransitionCompositionProps,
} from "@repo/video-engine-i2v/CompositionTransitionComposition";
import { useEditorStore } from "@/lib/stores/editor-store/editor-store";
import { cn } from "@/lib/utils";

type Props = {
  type: "START" | "END";
};

const startTransitionFormConfigs: TransitionFormConfig[] = [
  getCompositionTransitionBuilder("START", TransitionKind.enum.NONE)!
    .formConfig,
  getCompositionTransitionBuilder("START", TransitionKind.enum.FADE_BLACK)!
    .formConfig,
  getCompositionTransitionBuilder("START", TransitionKind.enum.FADE_WHITE)!
    .formConfig,
];

const endTransitionFormConfigs: TransitionFormConfig[] = [
  getCompositionTransitionBuilder("END", TransitionKind.enum.NONE)!.formConfig,
  getCompositionTransitionBuilder("END", TransitionKind.enum.FADE_BLACK)!
    .formConfig,
  getCompositionTransitionBuilder("END", TransitionKind.enum.FADE_WHITE)!
    .formConfig,
];

const useCompositionTransitionPreview =
  (type: CompositionTransitionType): UseTransitionPreview =>
  (transition: TransitionDto) => {
    const fps = 30;
    const compositionTransitionBuilder = getCompositionTransitionBuilder(
      type,
      transition.kind,
    );

    const durationInFrames =
      compositionTransitionBuilder.getPreviewClipDurationInFrames({
        transition,
        fps,
      });

    const inputProps: CompositionTransitionCompositionProps = {
      type,
      transition,
      clipDurationInFrames: durationInFrames,
    };

    return {
      Composition: CompositionTransitionComposition,
      durationInFrames,
      fps,
      inputProps,
    };
  };

export default function CompositionTransitionDialog({ type }: Props) {
  const [dialogKey, setDialogKey] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const video = useEditorStore((state) => state.video);
  const updateVideo = useEditorStore((state) => state.service.updateVideo);

  const setTransition = useCallback(
    (transition: TransitionDto) => {
      updateVideo((video) => {
        if (type === "START") {
          video.compositionJson.startTransition = transition;
        } else {
          video.compositionJson.endTransition = transition;
        }
      });
    },
    [type, updateVideo],
  );
  const currentTransition =
    type === "START"
      ? video!.compositionJson.startTransition
      : video!.compositionJson.endTransition;

  const noneTransition = getCompositionTransitionBuilder(
    type,
    TransitionKind.enum.NONE,
  )!.formConfig.buildDefaultValue();

  const hasTransition =
    currentTransition != null &&
    currentTransition.kind !== TransitionKind.enum.NONE;

  const openDialog = () => {
    setDialogKey((key) => key + 1); // Hack to reset dialog state when it's opened
    setIsOpen(true);
  };

  return (
    <div
      className={cn(
        "absolute right-0 left-0 z-1 ml-8 flex h-3 items-center justify-center",
        type === "START" ? "bottom-full" : "top-full",
      )}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      {hasTransition ? (
        <TransitionTriggerButton hasTransition onClick={openDialog} />
      ) : (
        // Separate element so removing a transition hides it instantly, while
        // the separator class still fades it in/out on hover.
        <span
          className={cn(
            "flex items-center justify-center",
            type === "START"
              ? "start-transition-separator"
              : "end-transition-separator",
          )}
        >
          <TransitionTriggerButton hasTransition={false} onClick={openDialog} />
        </span>
      )}

      <TransitionDialog
        key={`transition-dialog-${dialogKey}`}
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        currentTransition={currentTransition ?? noneTransition}
        onTransitionChange={setTransition}
        transitionFormConfigs={
          type === "START"
            ? startTransitionFormConfigs
            : endTransitionFormConfigs
        }
        useTransitionPreview={useCompositionTransitionPreview(type)}
        noneKindLabel="No Transition"
      />
    </div>
  );
}
